/**
 * Diccionario único de colores de gafete.
 * Mapea cada "tipo" (categoría/badge_color) a su color hex y etiqueta visible.
 *
 * Consumido por:
 * - components/BadgeColorTable.tsx (tabla de referencia)
 * - components/DynamicPrintModal.tsx (barra de color en la vista previa)
 */

export interface BadgeGroup {
  color: string
  label: string
  types: string[]
  caras?: number
  boletos?: string
}

export const BADGE_GROUPS: BadgeGroup[] = [
  { color: "#004d95", label: "Azul",        types: ["visitante"] },
  { color: "#007740", label: "Verde",       types: ["Expositor"] },
  { color: "#1c2324", label: "Negro",       types: ["Speaker"] },
  { color: "#e50000", label: "Rojo",        types: ["Prensa"] },
  { color: "#dc7717", label: "Naranja",     types: ["Producción"] },
  { color: "#ffffff", label: "Blanco",      types: ["Montajista"] },
  { color: "#FFCC00", label: "Amarillo",    types: ["Staff"] },
  { color: "#713c96", label: "Morado",      types: ["Organizador"] },
  { color: "#009caa", label: "AzuL Claro",  types: ["Expo (sin confirmar)"] },
]

const HEX_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/

export const isValidHexColor = (color: string): boolean => HEX_REGEX.test(color)

const normalize = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()

/**
 * Resuelve un valor (badge_color o categoria) al hex correspondiente.
 *
 * Orden:
 * 1. Si ya es hex válido, lo devuelve tal cual.
 * 2. Busca coincidencia (case/acentos-insensible) contra `types`.
 * 3. Busca coincidencia contra `label` (por si guardan "Azul", "Verde", etc.).
 *
 * Devuelve `null` si no encuentra mapeo.
 */
export const resolveBadgeHex = (value: string | null | undefined): string | null => {
  if (!value) return null
  const raw = String(value).trim()
  if (!raw) return null

  if (isValidHexColor(raw)) return raw

  const needle = normalize(raw)

  for (const group of BADGE_GROUPS) {
    if (group.types.some((t) => normalize(t) === needle)) return group.color
  }
  for (const group of BADGE_GROUPS) {
    if (normalize(group.label) === needle) return group.color
  }
  return null
}

/**
 * Devuelve la etiqueta legible asociada al color hex (o al tipo).
 */
export const resolveBadgeLabel = (value: string | null | undefined): string | null => {
  if (!value) return null
  const raw = String(value).trim()
  if (!raw) return null

  if (isValidHexColor(raw)) {
    const match = BADGE_GROUPS.find((g) => g.color.toLowerCase() === raw.toLowerCase())
    return match ? match.label : null
  }

  const needle = normalize(raw)
  const match = BADGE_GROUPS.find(
    (g) => g.types.some((t) => normalize(t) === needle) || normalize(g.label) === needle,
  )
  return match ? match.label : null
}

/**
 * Calcula color de texto (negro/blanco) según el brillo del fondo.
 */
export const getTextColorForBg = (hex: string): string => {
  const clean = hex.replace("#", "")
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  return (r * 299 + g * 587 + b * 114) / 1000 >= 128 ? "#000000" : "#ffffff"
}
